import { nanoid } from 'nanoid'

export type SchemaFieldType = 'string' | 'number' | 'integer' | 'boolean' | 'object' | 'array'

export interface SchemaFieldDraft {
  id: string
  name: string
  type: SchemaFieldType
  required: boolean
  description: string
  children: SchemaFieldDraft[]
  collapsed: boolean
}

export interface SchemaTypeItem {
  title: string
  value: SchemaFieldType
  icon: string
}

export const FACTORY_SCHEMA_TYPE_ITEMS: SchemaTypeItem[] = [
  { title: 'String', value: 'string', icon: 'mdi-format-text' },
  { title: 'Number', value: 'number', icon: 'mdi-numeric' },
  { title: 'Integer', value: 'integer', icon: 'mdi-numeric' },
  { title: 'Boolean', value: 'boolean', icon: 'mdi-toggle-switch-outline' },
  { title: 'Object', value: 'object', icon: 'mdi-code-json' },
  { title: 'Array', value: 'array', icon: 'mdi-format-list-bulleted' }
]

export function isSchemaFieldType(value: unknown): value is SchemaFieldType {
  return value === 'string' || value === 'number' || value === 'integer' || value === 'boolean' || value === 'object' || value === 'array'
}

export function createSchemaFieldDraft(type: SchemaFieldType = 'string', name = ''): SchemaFieldDraft {
  return {
    id: nanoid(),
    name,
    type,
    required: false,
    description: '',
    children: [],
    collapsed: false
  }
}

export function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isVisualizableFactorySchema(schema: Record<string, unknown>): boolean {
  const allowedRootKeys = new Set(['type', 'properties', 'required', 'additionalProperties'])
  const rootKeys = Object.keys(schema)
  if (rootKeys.some((key) => !allowedRootKeys.has(key))) return false
  if (schema.type !== 'object') return false
  if (!isPlainRecord(schema.properties)) return false
  if (schema.required != null && !isStringArray(schema.required)) return false
  if (schema.additionalProperties != null && typeof schema.additionalProperties !== 'boolean') return false
  return Object.values(schema.properties).every(isVisualizablePropertySchema)
}

export function schemaFieldsFromSchema(schema: Record<string, unknown>): SchemaFieldDraft[] {
  if (!isPlainRecord(schema.properties)) return []
  const requiredFields = stringArrayOrEmpty(schema.required)
  return schemaFieldsFromProperties(schema.properties, requiredFields)
}

export function schemaFromFields(fields: SchemaFieldDraft[]): Record<string, unknown> {
  const { properties, required } = schemaObjectFromFields(fields)
  return {
    type: 'object',
    properties,
    required,
    additionalProperties: false
  }
}

function schemaFieldsFromProperties(
  properties: Record<string, unknown>,
  requiredFields: string[]
): SchemaFieldDraft[] {
  return Object.entries(properties)
    .filter(([, property]) => isVisualizablePropertySchema(property))
    .map(([name, property]) => {
      const propertySchema = property as Record<string, unknown>
      const type = propertySchema.type
      return {
        id: nanoid(),
        name,
        type: isSchemaFieldType(type) ? type : 'string',
        required: requiredFields.includes(name),
        description: typeof propertySchema.description === 'string' ? propertySchema.description : '',
        children:
          type === 'object' && isPlainRecord(propertySchema.properties)
            ? schemaFieldsFromProperties(propertySchema.properties, stringArrayOrEmpty(propertySchema.required))
            : [],
        collapsed: false
      }
    })
}

function schemaObjectFromFields(fields: SchemaFieldDraft[]): {
  properties: Record<string, Record<string, unknown>>
  required: string[]
} {
  const properties: Record<string, Record<string, unknown>> = {}
  const required: string[] = []

  fields.forEach((field) => {
    const name = field.name.trim()
    if (!name) return

    const property: Record<string, unknown> = {
      type: field.type,
      ...(field.description.trim() ? { description: field.description.trim() } : {})
    }

    if (field.type === 'object') {
      const childObject = schemaObjectFromFields(field.children)
      property.properties = childObject.properties
      property.required = childObject.required
      property.additionalProperties = false
    }

    properties[name] = property
    if (field.required) {
      required.push(name)
    }
  })

  return { properties, required }
}

function isVisualizablePropertySchema(value: unknown): boolean {
  if (!isPlainRecord(value)) return false
  const type = value.type
  if (!isSchemaFieldType(type)) return false
  if (value.description != null && typeof value.description !== 'string') return false

  if (type !== 'object') {
    const allowedPropertyKeys = new Set(['type', 'description'])
    return Object.keys(value).every((key) => allowedPropertyKeys.has(key))
  }

  const allowedObjectKeys = new Set(['type', 'description', 'properties', 'required', 'additionalProperties'])
  if (Object.keys(value).some((key) => !allowedObjectKeys.has(key))) return false
  if (value.properties != null && !isPlainRecord(value.properties)) return false
  if (value.required != null && !isStringArray(value.required)) return false
  if (value.additionalProperties != null && typeof value.additionalProperties !== 'boolean') return false

  const properties = isPlainRecord(value.properties) ? value.properties : {}
  return Object.values(properties).every(isVisualizablePropertySchema)
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
}

function stringArrayOrEmpty(value: unknown): string[] {
  return isStringArray(value) ? value : []
}
